import React, { useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Check, Close, Delete } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from "@mui/material";

import { GenericOverview } from "../components/core/GenericOverview";
import { deleteFetch } from "../hooks/useFetch";
import {
    getEmptySmtpProfile,
    SmtpProfileEdit,
} from "../components/SmtpProfile/SmtpProfileEdit";
import { ISmtpProfile } from "../interfaces/ISmtpProfile";

export const SmtpProfiles: React.FC = () => {
    const [editProfile, setEditProfile] = useState(getEmptySmtpProfile());
    const [currentArray, setCurrentArray] = useState<ISmtpProfile[]>([]);
    const [profileToDelete, setProfileToDelete] = useState<ISmtpProfile | null>(null);

    const handleProfileChanged = (profile: ISmtpProfile) => {
        setCurrentArray(currentArray.map(x => x.id === profile.id ? profile : x));
    };

    const deleteWasSuccessfully = (wasSuccessfully: boolean) => {
        if (wasSuccessfully && profileToDelete !== null) {
            setCurrentArray(currentArray.filter(x => x.id !== profileToDelete.id));
        }
        setProfileToDelete(null);
    };

    const columns: GridColDef[] = [
        { field: "id", headerName: "Nr.", width: 80 },
        { field: "name", headerName: "Bezeichnung", flex: 1 },
        { field: "email", headerName: "Email", flex: 1 },
        { field: "server", headerName: "SMTP-Server", flex: 1 },
        { field: "port", headerName: "Port", width: 80 },
        {
            field: "tls",
            headerName: "TLS",
            width: 70,
            renderCell: (params: GridRenderCellParams<any, boolean>) =>
                params.value ? <Check /> : <Close />,
        },
        { field: "client_count", headerName: "Instanzen", width: 100 },
        {
            field: "updated_at",
            headerName: "Geändert",
            width: 170,
            valueGetter: (value) =>
                value ? new Date(value).toLocaleString("de-DE") : "–",
        },
        {
            field: "actions",
            headerName: "Aktionen",
            width: 80,
            renderCell: (params: GridRenderCellParams<ISmtpProfile>) => (
                <Tooltip title={params.row.client_count > 0 ? "Wird noch von Instanzen genutzt" : "Löschen"}>
                    <span>
                        <IconButton
                            disabled={params.row.client_count > 0}
                            onClick={(event) => {
                                event.stopPropagation();
                                setProfileToDelete(params.row);
                            }}
                        >
                            <Delete />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
    ];

    return (
        <GenericOverview
            title="SMTP-Profil"
            btnTitle="Neues Profil"
            columns={columns}
            editObject={editProfile}
            setEditObject={setEditProfile}
            currentArray={currentArray}
            setCurrentArray={setCurrentArray}
            fetchUrl="/smtpprofile/"
            getEmptyObject={getEmptySmtpProfile}
            enableDialog
            dialogWidth="md"
        >
            <>
                {profileToDelete !== null && (
                    <Dialog open onClose={() => setProfileToDelete(null)}>
                        <DialogTitle>Löschen bestätigen</DialogTitle>
                        <DialogContent>
                            Soll das SMTP-Profil „{profileToDelete.name}“ gelöscht werden?
                        </DialogContent>
                        <DialogActions>
                            <Button variant="outlined" onClick={() => setProfileToDelete(null)}>
                                Abbruch
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => deleteFetch("smtpprofile", profileToDelete.id, deleteWasSuccessfully)}
                            >
                                Löschen
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
                <SmtpProfileEdit
                    key={`smtpprofile-${editProfile.id}`}
                    currentProfile={editProfile}
                    setCurrentProfile={setEditProfile}
                    onProfileChanged={handleProfileChanged}
                />
            </>
        </GenericOverview>
    );
};
